import { Link } from "react-router-dom";
import {
  Award,
  Users,
  Globe2,
  ShieldCheck,
  Compass,
  Layers3,
  Wrench,
  Cpu,
  Box,
  Zap,
  Droplets,
  Wind,
  Building2,
  Sun,
  FileStack,
  Image as ImageIcon,
  ArrowUpRight,
} from "lucide-react";
import ContourBackground from "@/components/ContourBackground";
import useSeo from "@/hooks/useSeo";

const VALUES = [
  { icon: <Award className="h-5 w-5" />, t: "Survey-first thinking", d: "Built and led by licensed surveyors — our QC discipline carries into every CAD service line." },
  { icon: <Users className="h-5 w-5" />, t: "Embedded teams", d: "Your dedicated drafter or BIM modeler stays assigned across projects — no rotating juniors." },
  { icon: <Globe2 className="h-5 w-5" />, t: "Three-continent reach", d: "Overnight production aligned to USA, Canada, and Australia time zones." },
  { icon: <ShieldCheck className="h-5 w-5" />, t: "3-tier QC", d: "Drafter → senior reviewer → licensed surveyor / discipline lead — every drawing, every time." },
];

/**
 * Industries we serve. The page mirrors the same dual-catalog framing as the
 * Services / mega-menu: land-survey CAD is one of TWO equal practices, sitting
 * alongside architecture / structural / MEP / mechanical / civil / specialty CAD.
 */
const INDUSTRIES = [
  // — Land Survey CAD —
  {
    group: "Land Survey CAD",
    items: [
      { icon: <Compass className="h-5 w-5" />, t: "Land Surveying", d: "ALTA/NSPS, boundary, topo, plats, ROW, GIS, as-builts, floodplain ECs — for licensed survey firms." },
    ],
  },
  // — Architecture, Engineering & Construction (AEC) —
  {
    group: "Architecture, Engineering & Construction",
    items: [
      { icon: <Building2 className="h-5 w-5" />, t: "Architecture", d: "Permit sets, working drawings, elevations, sections, millwork — residential & commercial." },
      { icon: <Layers3 className="h-5 w-5" />, t: "Structural", d: "Rebar BBS, steel connection & shop, precast/prefab erection, fabrication drawings — AISC/ACI/Eurocode." },
      { icon: <Box className="h-5 w-5" />, t: "MEP & BIM", d: "Revit MEP, clash detection, BIM family creation, Scan-to-BIM, federated coordination." },
      { icon: <Wrench className="h-5 w-5" />, t: "Civil Engineering", d: "Road plan-profile, drainage, stormwater & urban infrastructure drafting (non-survey scope)." },
    ],
  },
  // — Mechanical / Electrical / Process —
  {
    group: "Mechanical, Electrical & Process",
    items: [
      { icon: <Cpu className="h-5 w-5" />, t: "Mechanical", d: "SolidWorks / Inventor / Creo 3D modeling, sheet-metal, assemblies, P&ID and PFD." },
      { icon: <Zap className="h-5 w-5" />, t: "Electrical", d: "Schematics, single-line diagrams, panel & wiring layouts, lighting & power-distribution plans." },
      { icon: <Droplets className="h-5 w-5" />, t: "Plumbing & Piping", d: "Piping isometrics, plumbing layouts, stack diagrams, fixture schedules." },
      { icon: <Wind className="h-5 w-5" />, t: "HVAC", d: "Duct routing & sizing, equipment schedules, hydronic piping & ventilation layouts." },
    ],
  },
  // — Specialty CAD —
  {
    group: "Specialty CAD",
    items: [
      { icon: <ImageIcon className="h-5 w-5" />, t: "3D Modeling & Rendering", d: "Photoreal stills, 3D walkthroughs, VR/AR-ready models for marketing & design review." },
      { icon: <FileStack className="h-5 w-5" />, t: "PDF-to-CAD / Conversion", d: "Raster-to-vector, paper-to-CAD, legacy DWG/DGN redraws and digitization." },
      { icon: <Sun className="h-5 w-5" />, t: "Solar PV Design", d: "Rooftop & ground-mount PV arrays, single-line diagrams, AHJ-compliant permit packages." },
    ],
  },
];

export default function About() {
  useSeo({
    title: "About — Tri-Axis XYZ CAD Solutions, LLC",
    description:
      "A CAD drafting and production studio serving land-surveying, architecture, structural, MEP/BIM, mechanical, electrical, civil and specialty engineering firms in the USA, Canada and Australia.",
  });
  return (
    <div data-testid="page-about">
      <Hero />
      <Industries />
      <Values />
      <Snapshot />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-200">
      <ContourBackground variant="light" opacity={0.55} seed={13} />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">About Tri-Axis XYZ</div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            A CAD partner for{" "}
            <span className="text-emerald-700">land survey</span>,{" "}
            <span className="text-orange-survey">architecture, structural &amp; MEP</span>{" "}
            teams.
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Tri-Axis XYZ CAD Solutions, LLC is a multi-discipline CAD drafting and production
            studio serving the United States, Canada and Australia. We started as a{" "}
            <strong className="text-slate-900">licensed land-survey</strong> production house
            and grew our bench to cover the broader engineering-CAD stack — architecture,
            structural detailing, MEP &amp; BIM coordination, mechanical, electrical, civil
            engineering, plumbing/piping, HVAC, 3D rendering, PDF-to-CAD and solar PV.
          </p>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Today we operate as a single embedded production unit: senior survey techies,
            licensed survey reviewers, BIM coordinators, structural detailers, mechanical
            modelers and rendering artists — all under one quality discipline. The result is
            faster turnarounds, predictable pricing, and drawings that feel like they came
            from your own office, regardless of the discipline.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/services" data-testid="about-cta-services" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white">
              See all 23 services →
            </Link>
            <Link to="/contact" data-testid="about-cta-hire-hero" className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2.5">
              Discuss your project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="corner-marks text-slate-900 border border-slate-200 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1640522635248-f1f0d92a50a2?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Surveyor at sunrise"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section id="about-industries" className="border-b border-slate-200 bg-paper-dark/20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Industries we serve</div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
              One bench.{" "}
              <span className="text-emerald-700">Every CAD discipline</span> a survey firm,
              architect, engineer or fabricator touches.
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600">
            Whether you need a sealed ALTA on Friday or a 600-sheet Revit MEP coordination
            model next quarter — the same team, standards and QC apply.
          </div>
        </div>

        <div className="space-y-10">
          {INDUSTRIES.map((group, gi) => (
            <div key={group.group} data-testid={`industry-group-${gi}`}>
              <div className="flex items-baseline justify-between mb-4">
                <div className="display text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
                  {group.group}
                </div>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {group.items.length} {group.items.length === 1 ? "discipline" : "disciplines"}
                </div>
              </div>
              <ul data-reveal="up" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {group.items.map((it) => (
                  <li
                    key={it.t}
                    data-testid={`industry-${it.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="bg-white p-6 tri-card"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center bg-topo text-white rounded-sm">
                      {it.icon}
                    </span>
                    <div className="display mt-4 text-base font-extrabold tracking-tight text-slate-900">
                      {it.t}
                    </div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/survey-techies"
            data-testid="about-cta-survey-techies"
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
          >
            🧭 Survey Techies →
          </Link>
          <Link
            to="/cad-techies"
            data-testid="about-cta-cad-techies"
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2.5 bg-white"
          >
            📐 CAD Techies (Beyond Survey) →
          </Link>
          <Link
            to="/services"
            data-testid="about-cta-services-grid"
            className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2.5"
          >
            Full service catalog <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Values() {
  return (
    <section id="about-values" className="border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Operating principles</div>
        <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
          How we run a <span className="text-emerald-700">multi-discipline studio</span>.
        </h2>
        <ul data-reveal="up" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {VALUES.map((v) => (
            <li key={v.t} className="bg-white p-6 tri-card">
              <span className="inline-flex h-10 w-10 items-center justify-center bg-emerald-700 text-white rounded-sm">{v.icon}</span>
              <div className="display mt-4 text-lg font-extrabold tracking-tight text-slate-900">{v.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Snapshot() {
  return (
    <section id="about-snapshot">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Company snapshot</div>
          <h2 className="display mt-3 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            At a glance.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Headquartered in Florida, USA — delivering globally across 23 CAD service lines
            spanning land survey, AEC, mechanical, electrical and specialty disciplines.
          </p>
          <Link to="/contact" data-testid="about-cta-contact" className="mt-6 inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-5 py-3">
            Get in touch
          </Link>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {[
            { k: "2018", v: "Founded" },
            { k: "23", v: "Service lines" },
            { k: "120+", v: "Firms served" },
            { k: "5K+", v: "Drawings shipped" },
            { k: "3", v: "Continents covered" },
            { k: "<24h", v: "First response" },
          ].map((s) => (
            <div key={s.v} className="bg-white p-6">
              <div className="display text-3xl font-extrabold tracking-tighter text-topo">{s.k}</div>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
